import { Container } from '@/components/ui/Container'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import {
  t,
  getCurrentLanguage,
  localizedText,
  localizedArray,
  type BilingualText,
  type BilingualArray,
} from '@/utils/i18n'
import { getPositionById, getVisiblePositions, getEffectiveStatus, type Position } from '@/config/career'
import { octoberPostingDetails } from '@/config/careerOctober'
import type { Milestone, LabeledRow, PositionDetail, ReasonBlock } from '@/config/careerDetail'
import { trackEvent } from '@/utils/analytics'

interface CareerDetailPageProps {
  positionId: string
}

/**
 * Company milestones — identical for every posting, so they live here once.
 * Kept in sync with the Saramin postings built by HR/build_web_postings.py.
 *
 * Wording rule: the two demonstration plants are **never** referred to as one
 * "combined" facility — different media, temperature ranges and purposes
 * (confirmed 2026-06-02).
 */
const companyMilestones: Milestone[] = [
  {
    date: '2025.09',
    text: { en: 'Seed funding raised', ko: 'Seed 투자 유치' },
  },
  {
    date: '2025.09',
    text: {
      en: 'Selected for Deep-Tech TIPS — building two demonstration plants on our own site (2025.09–2028.08)',
      ko: '딥테크 팁스 선정 — 자체 부지 실증 설비 2기 구축 과제 (2025.09–2028.08)',
    },
  },
  {
    date: '2025.12',
    text: {
      en: 'Selected for KOICA CTS Seed 2 — district heating in Tsetserleg, Arkhangai Province, Mongolia (KRW 700 million)',
      ko: 'KOICA CTS Seed 2 선정 — 몽골 아르항가이주 체체를렉 지역 난방 사업 (총 7억원)',
    },
  },
  {
    date: '2026.04',
    text: { en: 'Follow-on seed funding raised', ko: '후속 Seed 투자 유치' },
  },
  {
    date: { en: 'In progress', ko: '진행 중' },
    text: {
      en: 'Building two demonstration plants on the Jukdong site — 5 MWh (200°C) and 0.7 MWh (500°C); core equipment targeted for completion in H2 2026',
      ko: '죽동 부지 실증 설비 2기 구축 — 5 MWh(200℃) · 0.7 MWh(500℃), 2026년 하반기 핵심 설비 완료 목표',
    },
  },
  {
    date: { en: 'In progress', ko: '진행 중' },
    text: {
      en: 'Local installation and commissioning of 6 MWh in Tsetserleg, Mongolia — four times the Seed 1 result (1.5 MWh, 188 beneficiaries), powered entirely by solar',
      ko: '몽골 체체를렉 6 MWh 현지 설치·시운전 — Seed 1 실적(1.5 MWh · 188명 수혜) 대비 4배 확장, 100% 태양광 연계',
    },
  },
]

/** Working conditions — same for every posting. */
const commonConditions: LabeledRow[] = [
  {
    label: { en: 'Location', ko: '근무지' },
    value: {
      en: '135 Jukdong-ro, Yuseong-gu, Daejeon (HQ + corporate R&D institute)',
      ko: '대전광역시 유성구 죽동로 135 (본사 + 기업부설연구소)',
    },
  },
  {
    label: { en: 'Employment', ko: '고용 형태' },
    value: {
      en: 'Full-time (3-month probation, same terms)',
      ko: '정규직 (수습 3개월, 동일 처우)',
    },
  },
  {
    label: { en: 'Salary', ko: '연봉' },
    value: { en: 'Negotiable', ko: '추후 협의' },
  },
]

/**
 * Shared "How to apply" rows for the postings reopened as open-until-filled
 * (originally the August 2026 intake). `subject` differs per posting, so it
 * is spliced in by `applyRows()`.
 */
function applyRows(subject: BilingualText): LabeledRow[] {
  return [
    {
      label: { en: 'Application period', ko: '모집 기간' },
      value: {
        en: 'Rolling — open until the position is filled',
        ko: '상시 접수 — 채용 시 마감',
      },
    },
    {
      label: { en: 'Email', ko: '이메일' },
      value: { en: 'hr@gigaette.com', ko: 'hr@gigaette.com' },
    },
    { label: { en: 'Subject format', ko: '제목 형식' }, value: subject },
    {
      label: { en: 'Attachments', ko: '첨부 파일' },
      value: {
        en: 'Resume (PDF) + cover letter + portfolio',
        ko: '이력서(PDF) + 자기소개서 + 포트폴리오',
      },
    },
    {
      label: { en: 'Recruiter', ko: '채용 담당' },
      value: {
        en: 'Ikje Jang (042-719-7544, hr@gigaette.com)',
        ko: '장익제 프로 (042-719-7544, hr@gigaette.com)',
      },
    },
  ]
}

/** Hiring process — same five steps for every posting. */
const commonProcess: PositionDetail['process'] = {
  steps: {
    en: ['Application', 'Technical interview', 'Executive interview', 'Offer & terms', 'Onboarding'],
    ko: ['서류', '실무면접', '임원면접', '처우협의', '입사'],
  },
  note: {
    en: 'Total: about 3 weeks · Applications are reviewed as they arrive and everyone hears back individually',
    ko: '총 소요: 약 3주 · 지원서는 접수 순으로 검토하며 결과는 개별 안내드립니다',
  },
}

/**
 * Full job-posting content, bilingual. Keyed by Position.id.
 * Add a new entry here whenever a posting is added to `config/career.ts`.
 * Source of truth for both languages — switching the site language switches
 * the posting language automatically.
 */
const postingDetails: Record<string, PositionDetail> = {
  ...octoberPostingDetails,
  't1-senior': {
    eyebrow: {
      en: 'Deep-Tech Thermal Energy Storage',
      ko: 'Deep-Tech Thermal Energy Storage',
    },
    subtitle: {
      en: 'Thermal Systems Team · Experienced Hire (5–10 yrs)',
      ko: '열시스템 팀 · 경력직 채용 (경력 5–10년)',
    },
    email: 'hr@gigaette.com',
    applySubject: {
      en: '[Apply] Experienced-SeniorThermalSystemsEngineer-',
      ko: '[지원] 경력-열시스템책임엔지니어-',
    },
    mission: {
      en: 'Lead decision-maker for HTS/IU-IsoTES® media and cycle design. Drive the core IsoTES® IP alongside the CTO.',
      ko: 'HTS/IU-IsoTES® 매체·사이클 설계의 의사결정 책임자. CTO와 함께 IsoTES® IP 핵심을 끌고 갑니다.',
    },
    responsibilities: {
      en: [
        'Design thermal-storage media and cycles for glycerin (200°C) and solar salts (550°C)',
        'Validate integrated cycles across heat exchangers, absorption chillers, ORC, steam generators, and hot-air generators',
        'Own thermal-performance guarantees for two demonstration plants (5 MWhr and 0.7 MWhr)',
        'Set operating margins based on media corrosion and degradation data',
        'Assess thermal suitability for overseas PoCs (Mongolia) and new markets',
      ],
      ko: [
        '글리세린(200℃) / Solar Salts(550℃) 매체·열저장 사이클 설계',
        '열교환기·흡수식냉동기·ORC·Steam Generator·Hot Air Generator 통합 사이클 검증',
        '실증 설비 2기 (5 MWhr · 0.7 MWhr) 열성능 보증 책임',
        '매체 부식·열화 데이터 기반 운전 마진 설정',
        '해외 PoC (몽골) 및 신규 시장 열적 적합성 평가',
      ],
    },
    requirements: {
      en: [
        "Bachelor's or higher in Mechanical, Chemical, or Energy Engineering (Master's/PhD preferred)",
        '5+ years in thermal energy systems or chemical-plant thermal engineering',
        'Hands-on experience designing or simulating heat exchangers, HTF, and thermal cycles',
        'Ability to write technical documents in Korean',
      ],
      ko: [
        '기계·화공·에너지공학 학사 이상 (석·박사 우대)',
        '열에너지 시스템 또는 화학플랜트 열공학 5년 이상',
        'HX·HTF·열사이클 설계 또는 시뮬레이션 실무 경험',
        '한국어 기술문서 작성 능력',
      ],
    },
    preferred: {
      en: [
        'Experience in TES (thermal storage), CSP, heat pumps, or ORC',
        'Experience leading government R&D projects (PI level)',
        'Simulation tools: Aspen, EES, MATLAB, Modelica',
        'Familiarity with pressure-vessel, KGS, and high-temperature fluid safety codes',
        'English proficiency (for overseas PoCs and foreign component specs)',
      ],
      ko: [
        'TES(축열)·CSP·열펌프·ORC 분야 경험',
        '정부 R&D 과제 책임자급 수행 경험',
        'Aspen·EES·MATLAB·Modelica 시뮬레이션 도구',
        '압력용기·KGS·고온유체 안전 코드 이해',
        '영어 가능 (해외 PoC·해외 부품 사양 대응)',
      ],
    },
    milestones: companyMilestones,
    conditions: commonConditions,
    apply: [
      {
        label: { en: 'Email', ko: '이메일' },
        value: { en: 'hr@gigaette.com', ko: 'hr@gigaette.com' },
      },
      {
        label: { en: 'Subject format', ko: '제목 형식' },
        value: {
          en: '[Apply] Experienced-SeniorThermalSystemsEngineer-YourName',
          ko: '[지원] 경력-열시스템책임엔지니어-성함',
        },
      },
      {
        label: { en: 'Attachments', ko: '첨부 파일' },
        value: {
          en: 'Resume (PDF) + cover letter + portfolio',
          ko: '이력서(PDF) + 자기소개서 + 포트폴리오',
        },
      },
      {
        label: { en: 'Recruiter', ko: '채용 담당' },
        value: {
          en: 'Ikje Jang (042-719-7544, hr@gigaette.com)',
          ko: '장익제 프로 (042-719-7544, hr@gigaette.com)',
        },
      },
    ],
    process: {
      steps: commonProcess.steps,
      note: { en: 'Total: about 3 weeks', ko: '총 소요: 약 3주' },
    },
  },

  // ─── August 2026 intake — reopened 2026-08-24 as open until filled ────────
  // Source of truth for the Korean text: HR/build_web_postings.py → POSITIONS
  // (the same copy that goes to Saramin). English is written to the tone guide
  // in the vault's Recruiting Copy note.

  'pressure-vessel-tank-design-engineer': {
    eyebrow: {
      en: 'Deep-Tech Thermal Energy Storage',
      ko: 'Deep-Tech Thermal Energy Storage',
    },
    subtitle: {
      en: 'Corporate R&D Institute · Experienced Hire (3–8 yrs) · Tank structural design',
      ko: '기업부설연구소 · 경력 3–8년 · 탱크 구조 강도 설계',
    },
    email: 'hr@gigaette.com',
    applySubject: {
      en: '[Apply] PressureVesselTankDesignEngineer-',
      ko: '[지원] 압력용기탱크설계엔지니어-',
    },
    saraminUrl: 'https://www.saramin.co.kr/zf_user/jobs/view?rec_idx=54637161',
    mission: {
      en: "You take the CTO's P&ID and design the structural strength of the pressure vessels and tanks yourself. The thickness and materials you calculate go up on site as IsoTES® demonstration plants.",
      ko: 'CTO의 P&ID를 받아 압력용기·탱크의 구조 강도를 직접 설계합니다. 당신이 계산한 두께와 재질이 IsoTES® 실증 설비로 현장에 그대로 세워집니다.',
    },
    responsibilities: {
      en: [
        "Design shell and head thickness, materials, nozzles and reinforcement for the tanks, starting from the CTO's P&ID",
        'Verify structural safety at operating temperature and pressure against the applicable codes',
        'Specify piping and pressure components, and manage design changes',
      ],
      ko: [
        'CTO의 P&ID를 받아 탱크 동체·헤드 두께, 재질, 노즐·보강 설계',
        '운전 온도·압력 조건에서 구조 안전성 검토 (코드 기준)',
        '배관·압력 부품 사양 결정 및 설계 변경 관리',
      ],
    },
    requirements: {
      en: [
        'Hands-on strength design of pressure vessels and piping under ASME, KGS or KS codes',
        'Mechanical engineering background (strength of materials, pressure-vessel design)',
        'Work you designed yourself that was actually built and delivered',
      ],
      ko: [
        '압력용기·배관 강도 설계 실무 경험 (ASME·KGS·KS 코드 적용)',
        '기계공학 베이스 (재료역학·압력용기 설계 이해)',
        '본인이 설계해 실제 제작·납품된 결과물이 있는 분',
      ],
    },
    preferred: {
      en: [
        'Insulation or high-temperature fluid system design',
        'Thermal and structural FEA (Ansys, Abaqus)',
        'Mechanical drafting and GD&T',
        'Plant or fabrication-shop experience',
      ],
      ko: [
        '단열·고온 유체 시스템 설계 경험',
        'FEA 열·구조 해석 경험 (Ansys·Abaqus)',
        '기계제도(GD&T) 이해',
        '플랜트·제작 현장 경험',
      ],
    },
    whyThisRole: {
      title: {
        en: 'The number of tanks to design keeps growing',
        ko: '설계해야 할 탱크가 늘어나고 있습니다',
      },
      body: {
        en: 'Deep-Tech TIPS selection and follow-on funding have us building two demonstration plants on our own site, and in Mongolia a 6 MWh district-heating plant is already in local installation. We have moved from designing one vessel to designing several, and we need someone to share the structural design work. You would not be joining a team that is already set — you are joining at the moment the design function takes shape.',
        ko: '딥테크 팁스 선정과 후속 투자로 자체 부지에 실증 설비 2기를 세우고 있고, 몽골에서는 6 MWh 지역난방 설비가 이미 현지 설치 단계에 들어가 있습니다. 설계 대상이 한 기에서 여러 기로 늘어나는 단계에 들어섰고, 그만큼 구조 설계를 함께 책임질 사람이 필요해졌습니다. 이미 만들어진 조직에 들어오는 것이 아니라, 설계 기능이 자리를 잡는 순간에 합류하는 자리입니다.',
      },
    },
    yourImpact: {
      title: {
        en: "One plant's design becomes the standard for the next",
        ko: '한 기의 설계가 다음 설비의 표준이 됩니다',
      },
      body: {
        en: 'The thicknesses, materials and reinforcement approach you validate do not stop at one plant — they carry into the next unit and into the plants we build overseas. Once that design knowledge sits in-house, new projects no longer start from scratch, and we can take on more sites, faster. This role sets the pace at which IsoTES® spreads.',
        ko: '당신이 검증한 두께·재질·보강 방식은 그 설비 한 기에서 끝나지 않고 다음 호기와 해외 적용 설비로 그대로 이어집니다. 설계 자산이 사내에 쌓이면 새 프로젝트마다 처음부터 계산하지 않아도 되고, 회사는 그만큼 더 많은 현장을 더 빠르게 받을 수 있습니다. IsoTES®가 퍼져 나가는 속도를 실제로 만드는 자리입니다.',
      },
    },
    whatYouGet: {
      title: {
        en: 'Your scope grows as the fleet grows',
        ko: '설비가 늘어나는 만큼 역할도 함께 커집니다',
      },
      body: {
        en: 'The thickness and materials you decide stand on site as real hardware a few months later. You design several storage tanks spanning temperatures from ambient to 550°C molten salt, and you go through the full cycle — code compliance (ASME, KGS), fabricator coordination, and commissioning-data feedback. You come out as someone who was there from the start of the move from demonstration to commercial, with a scope that widens as the fleet does.',
        ko: '본인이 정한 두께와 재질이 몇 달 뒤 현장에 실물로 세워집니다. 상온부터 550℃ 용융염까지 온도 대역이 다른 저장 탱크를 여러 기 설계하고, 코드 대응(ASME·KGS)·제작사 협의·시운전 데이터 피드백까지 한 사이클을 통째로 경험합니다. 회사가 실증에서 상용으로 넘어가는 구간을 처음부터 함께 지나온 사람으로 남고, 설비가 늘어나는 만큼 맡을 수 있는 범위도 같이 넓어집니다.',
      },
    },
    milestones: companyMilestones,
    conditions: commonConditions,
    apply: applyRows({
      en: '[Apply] PressureVesselTankDesignEngineer-YourName',
      ko: '[지원] 압력용기탱크설계엔지니어-성함',
    }),
    process: commonProcess,
  },

  'thermal-energy-storage-modeler': {
    eyebrow: {
      en: 'Deep-Tech Thermal Energy Storage',
      ko: 'Deep-Tech Thermal Energy Storage',
    },
    subtitle: {
      en: 'Corporate R&D Institute · Open to all experience levels and majors · 3D modelling and detail drawings',
      ko: '기업부설연구소 · 경력 무관 · 전공 무관 · 3D 모델링·상세 도면',
    },
    email: 'hr@gigaette.com',
    applySubject: {
      en: '[Apply] ThermalEnergyStorageSystemModeler-',
      ko: '[지원] 열에너지저장시스템모델러-',
    },
    saraminUrl: 'https://www.saramin.co.kr/zf_user/jobs/view?rec_idx=54638006',
    mission: {
      en: 'You take the values the design engineer sets and turn them into 3D models and detailed drawings, and you manage schedule and quality with the fabrication vendors yourself. The drawings you produce go up on site as demonstration plants.',
      ko: '설계 엔지니어가 정한 값을 받아 3D 모델과 상세 도면으로 구현하고, 외주 제작업체와의 일정·품질을 직접 관리합니다. 당신이 그린 도면이 실증 설비 현장에 그대로 세워집니다.',
    },
    responsibilities: {
      en: [
        'Build 3D models and detailed drawings from the values set by the design engineer',
        'Prepare and maintain the bill of materials',
        'Place fabrication orders from the drawings and manage lead time and quality (vendor pricing and contracts are handled separately in-house)',
        'Review vendor drawings and handle the technical back-and-forth',
      ],
      ko: [
        '설계 엔지니어가 정한 값을 받아 3D 모델링 및 상세 도면 작성',
        'BOM(부품 목록) 작성·관리',
        '도면 기반 제작 외주 발주·납기·품질 관리 (벤더 단가·계약은 사내 별도 담당)',
        '외주 제작업체 도면 검증 및 기술 소통',
      ],
    },
    requirements: {
      en: [
        'Experience producing real drawings in 2D/3D design tools (SolidWorks, CATIA, AutoCAD or similar)',
        'Drawings or models you made yourself that you can show us',
        'Experience exchanging schedules and deliverables with outside vendors',
      ],
      ko: [
        '2D·3D 설계 툴로 실제 도면을 그려본 경험 (Solidworks·CATIA·AutoCAD 등)',
        '직접 그린 도면이나 모델링 결과물이 있는 분',
        '외주 업체와 일정·결과물을 주고받아본 경험',
      ],
    },
    preferred: {
      en: [
        'Insulation or high-temperature fluid system design',
        'Mechanical drafting and GD&T',
        'Plant or fabrication-shop experience',
      ],
      ko: [
        '단열·고온 유체 시스템 설계 경험',
        '기계제도(GD&T) 이해',
        '플랜트·제작 현장 경험',
      ],
    },
    whyThisRole: {
      title: {
        en: 'The site has started running in earnest',
        ko: '현장이 본격적으로 돌아가기 시작했습니다',
      },
      body: {
        en: 'As construction of the demonstration plants hits full stride, the drawings to produce and the parts to order are piling up fast. We have reached the point where someone needs to own the link between design and fabrication full-time, which is why this role is open. More than your major or your years of experience, we are looking for someone who sees a drawing through to the end and stays on top of a vendor to the end.',
        ko: '실증 설비 구축이 본궤도에 오르면서 그려야 할 도면과 발주해야 할 부품이 빠르게 늘고 있습니다. 설계와 제작 사이를 전담해서 이어 줄 사람이 필요한 단계가 됐고, 그래서 이 자리를 엽니다. 전공이나 연차보다 도면을 끝까지 그려내고 외주를 끝까지 챙기는 분을 찾습니다.',
      },
    },
    yourImpact: {
      title: {
        en: 'How fast we build is how fast the company moves',
        ko: '구축 속도가 곧 회사의 속도입니다',
      },
      body: {
        en: 'When the design → drawing → order → delivery cycle gets shorter, the demonstration plants stand up sooner and the engineers get to stay on design. At this stage the build schedule is the company schedule, so pulling that cycle in by a few days shows up directly as company-wide speed. Your contribution is visible as hardware.',
        ko: '설계 → 도면 → 발주 → 입고로 이어지는 사이클이 짧아지면 실증 설비가 서는 시점이 그만큼 앞당겨지고, 엔지니어들은 설계에 더 집중할 수 있게 됩니다. 설비 구축 일정이 곧 회사의 일정인 단계라, 이 사이클을 며칠 당기는 일이 회사 전체의 속도로 그대로 이어집니다. 기여가 결과물로 바로 보이는 자리입니다.',
      },
    },
    whatYouGet: {
      title: {
        en: 'You learn where a drawing actually ends',
        ko: '도면 한 장의 끝까지 아는 사람이 됩니다',
      },
      body: {
        en: 'You follow your own drawings all the way through cutting, welding and erection on site. You pick up the language of the shop floor — tolerance, manufacturability, lead time — against real hardware, and as your skills build there is a path to growing into a design engineer. You go through the period when we scale from one plant to several, and your scope widens as the company does.',
        ko: '본인이 그린 도면이 절단되고, 용접되고, 현장에 세워지는 전 과정을 따라갑니다. 공차·가공성·납기 같은 제작 현장의 언어를 실물로 익히게 되고, 역량이 쌓이면 설계 엔지니어로 성장할 수 있는 경로도 열려 있습니다. 설비가 한 기에서 여러 기로 늘어나는 시기를 처음부터 함께 지나면서, 회사가 커지는 만큼 맡을 수 있는 범위도 같이 넓어집니다.',
      },
    },
    milestones: companyMilestones,
    conditions: commonConditions,
    apply: applyRows({
      en: '[Apply] ThermalEnergyStorageSystemModeler-YourName',
      ko: '[지원] 열에너지저장시스템모델러-성함',
    }),
    process: commonProcess,
  },

  'electrical-control-engineer': {
    eyebrow: {
      en: 'Deep-Tech Thermal Energy Storage',
      ko: 'Deep-Tech Thermal Energy Storage',
    },
    subtitle: {
      en: 'Corporate R&D Institute · 3+ years experience (hands-on electrical work required)',
      ko: '기업부설연구소 · 경력 3년 이상 (전기 실무 필수)',
    },
    email: 'hr@gigaette.com',
    applySubject: {
      en: '[Apply] ElectricalControlEngineer-',
      ko: '[지원] 전기제어엔지니어-',
    },
    saraminUrl: 'https://www.saramin.co.kr/zf_user/jobs/view?rec_idx=54638080',
    mission: {
      en: 'Starting from the control concept the CTO sets, you own the whole electrical and control scope yourself — panel ratings, drawings, PLC implementation. The panels and logic you put together are what complete the electrical control of our IsoTES® demonstration plants.',
      ko: 'CTO가 잡은 제어 개념을 받아, 판넬 정격 산정부터 도면·PLC 구현까지 전기·제어 전 과정을 직접 맡습니다. 당신이 구성하는 판넬과 로직이 IsoTES® 실증 설비의 전기 제어를 완성합니다.',
    },
    responsibilities: {
      en: [
        'Size panel components — MCCB ratings, cable sizing, protection coordination',
        'Produce switchboard and panel drawings (EPLAN, AutoCAD Electrical or similar)',
        'Implement PLC control logic — the concept and core logic come from the CTO',
        'Build HMI screens and support commissioning',
        'Review panel-builder drawings and handle the technical back-and-forth',
      ],
      ko: [
        '판넬 부품 정격 산정 — MCCB 용량, 케이블 굵기, 차단 협조',
        '배전반(판넬) 도면 작성 (EPLAN·AutoCAD Electrical 등)',
        'PLC 제어로직 구현 — 개념·핵심 로직은 사내(CTO) 제공',
        'HMI 화면 구성 및 시운전 보조',
        '판넬 제작업체 도면 검증·소통',
      ],
    },
    requirements: {
      en: [
        'Experience designing or building low-voltage panels and switchboards (breaker and cable sizing)',
        'Electrical drawings you have produced yourself (EPLAN, AutoCAD Electrical or similar)',
        'PLC programming you have actually run on real equipment',
        'Korean Engineer or Industrial Engineer certification in electrical engineering, or equivalent',
      ],
      ko: [
        '저압 판넬·배전반 설계 또는 제작 경험 (차단기·케이블 정격 산정)',
        '전기 도면을 직접 그려본 경험 (EPLAN·AutoCAD Electrical 등)',
        'PLC 프로그래밍 경험 (직접 돌려본 결과물)',
        '전기기사·전기산업기사 등 자격',
      ],
    },
    preferred: {
      en: [
        'Hands-on PLC and HMI implementation on industrial sites',
        'Electrical design around explosion-proof or high-voltage equipment',
        'Commissioning experience on energy or plant equipment',
      ],
      ko: [
        '산업 현장 PLC·HMI 실무 구현 경험',
        '방폭·고압 설비 인근 전기 설계 경험',
        '에너지·플랜트 설비 시운전 참여 경험',
      ],
    },
    whyThisRole: {
      title: {
        en: 'The electrical and control workload is growing fast',
        ko: '전기·제어로 챙길 대상이 빠르게 늘고 있습니다',
      },
      body: {
        en: 'With the two demonstration plants in Jukdong and the Mongolian site running at the same time, the ratings to size, the drawings to produce and the logic to verify are all growing together. Each plant has a different heat source and temperature range, so the same judgement cannot simply be copied across. We have reached the point where we need an engineer to take electrical and control as one axis and carry it through, which is why this role is open.',
        ko: '죽동 실증 설비 2기와 몽골 현장이 동시에 돌아가면서, 정격을 잡고 도면을 그리고 로직을 확인해야 할 일이 함께 늘고 있습니다. 설비마다 열원과 온도 조건이 달라 같은 판단을 그대로 옮겨 쓸 수도 없습니다. 전기·제어를 하나의 축으로 놓고 끝까지 끌고 갈 엔지니어가 필요해진 시점이고, 그래서 이 자리를 엽니다.',
      },
    },
    yourImpact: {
      title: {
        en: 'The standard you set once carries into the next plant',
        ko: '한 번 잡아 둔 기준이 다음 설비로 이어집니다',
      },
      body: {
        en: 'Once the sizing criteria and the control logic are settled, the next plant starts from there. As the fleet grows we stop re-deriving everything from scratch, and we can take on more sites, faster. In a company building plant after plant, that accumulation comes back as visible speed from the second unit onward.',
        ko: '정격 산정 기준과 제어 로직이 한 번 정리되면 다음 설비는 그 위에서 시작합니다. 설비가 늘어날수록 매번 처음부터 따지지 않아도 되고, 회사는 그만큼 더 많은 현장을 더 빠르게 받을 수 있습니다. 설비를 여러 기 세워 가는 회사에서 이 축적은 2호기부터 눈에 보이는 속도 차이로 돌아옵니다.',
      },
    },
    whatYouGet: {
      title: {
        en: 'You define the electrical design of plants going up for the first time',
        ko: '처음 서는 설비의 전기를 직접 정의합니다',
      },
      body: {
        en: 'This is not a role maintaining plants that already exist — you decide the electrical configuration of plants being built for the first time. You run from sizing and drawings through PLC logic, commissioning and operating data as one person, and you work with control targets you rarely meet in an ordinary factory: high-temperature fluids and multiple heat sources. You go through the stretch where the domestic demonstration plants and the Mongolian site run in parallel, building experience at the same pace as the company.',
        ko: '이미 지어진 설비를 지키는 자리가 아니라, 처음 세워지는 설비의 전기 구성을 본인이 정하는 자리입니다. 정격 산정과 도면에서 시작해 PLC 로직, 시운전, 운전 데이터 확인까지 한 사람이 관통하고, 고온 유체·다중 열원처럼 일반 공장에서는 만나기 어려운 제어 대상을 다룹니다. 국내 실증 설비와 몽골 현장이 동시에 돌아가는 구간을 함께 지나면서, 회사의 성장과 같은 속도로 경험이 쌓이는 자리입니다.',
      },
    },
    milestones: companyMilestones,
    conditions: commonConditions,
    apply: applyRows({
      en: '[Apply] ElectricalControlEngineer-YourName',
      ko: '[지원] 전기제어엔지니어-성함',
    }),
    process: commonProcess,
  },
}

export function CareerDetailPage({ positionId }: CareerDetailPageProps): HTMLElement {
  const position = getPositionById(positionId)
  const detail = postingDetails[positionId]

  if (!position || !detail) {
    return createNotFound()
  }

  const main = document.createElement('div')
  main.className = 'bg-white'

  main.appendChild(createHero(position, detail))
  main.appendChild(createBody(position, detail))

  trackEvent('view_career', { position_id: positionId })

  return main
}

function sectionTitle(key: string): HTMLElement {
  const h = document.createElement('h2')
  h.className = 'text-2xl font-bold text-slate-900 mb-6'
  h.textContent = t(`career.detail.sections.${key}`)
  return h
}

function createHero(position: Position, detail: PositionDetail): HTMLElement {
  const section = document.createElement('section')
  section.className = 'pt-32 pb-16 bg-gradient-to-b from-slate-900 to-slate-800 text-white'

  const content = document.createElement('div')
  content.className = 'max-w-4xl mx-auto'

  // Back link
  const backLink = document.createElement('a')
  backLink.href = '/company/career'
  backLink.className = 'inline-flex items-center text-slate-400 hover:text-white mb-6 transition-colors'
  backLink.innerHTML = `<svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg> ${t('career.detail.backToList')}`
  backLink.addEventListener('click', (e) => {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('navigate', { detail: '/company/career' }))
  })
  content.appendChild(backLink)

  // Eyebrow
  const eyebrow = document.createElement('p')
  eyebrow.className = 'text-sm font-semibold text-orange-400 tracking-wide uppercase mb-3'
  eyebrow.textContent = localizedText(detail.eyebrow)
  content.appendChild(eyebrow)

  // Title
  const title = document.createElement('h1')
  title.className = 'text-4xl md:text-5xl font-semibold mb-4 pb-1'
  title.style.background = 'linear-gradient(to right, #ffffff, #cbd5e1)'
  title.style.webkitBackgroundClip = 'text'
  title.style.backgroundClip = 'text'
  title.style.webkitTextFillColor = 'transparent'
  title.textContent = localizedText(position.title)
  content.appendChild(title)

  // Subtitle
  const subtitle = document.createElement('p')
  subtitle.className = 'text-lg text-slate-300 mb-6'
  subtitle.textContent = localizedText(detail.subtitle)
  content.appendChild(subtitle)

  // Badge + location + deadline row. The internal track code is not shown to
  // candidates.
  const meta = document.createElement('div')
  meta.className = 'flex flex-wrap items-center gap-3'

  const status = getEffectiveStatus(position)
  if (status === 'urgent') {
    meta.appendChild(Badge({ text: t('career.urgentBadge'), color: 'orange', variant: 'solid' }))
  } else if (status === 'closed') {
    meta.appendChild(Badge({ text: t('career.closedBadge'), color: 'gray', variant: 'solid' }))
  }

  const location = document.createElement('span')
  location.className = 'text-sm text-slate-400'
  location.textContent = `${t('career.location')}: ${localizedText(position.location)}`
  meta.appendChild(location)

  const deadline = document.createElement('span')
  deadline.className = 'text-sm text-slate-400'
  deadline.textContent = `${t('career.deadline')}: ${status === 'closed' && position.deadline === null ? t('career.closedBadge') : formatDeadline(position.deadline)}`
  meta.appendChild(deadline)

  content.appendChild(meta)

  section.appendChild(Container({ children: content }))
  return section
}

function createBody(position: Position, detail: PositionDetail): HTMLElement {
  const section = document.createElement('section')
  section.className = 'py-16 bg-white'

  const content = document.createElement('div')
  content.className = 'max-w-4xl mx-auto'

  // Mission (highlight)
  const mission = document.createElement('div')
  mission.className = 'mb-14 p-8 rounded-2xl bg-orange-50 border border-orange-100'
  const missionLabel = document.createElement('p')
  missionLabel.className = 'text-sm font-semibold text-orange-600 uppercase tracking-wide mb-2'
  missionLabel.textContent = t('career.detail.sections.mission')
  const missionText = document.createElement('p')
  missionText.className = 'text-xl text-slate-800 leading-relaxed font-medium'
  missionText.textContent = localizedText(detail.mission)
  mission.appendChild(missionLabel)
  mission.appendChild(missionText)
  content.appendChild(mission)

  // Responsibilities
  content.appendChild(createListBlock('responsibilities', detail.responsibilities, 'disc'))
  // Requirements
  content.appendChild(createListBlock('requirements', detail.requirements, 'disc'))
  // Preferred (check marks)
  content.appendChild(createListBlock('preferred', detail.preferred, 'check'))
  if (detail.companyIntro) {
    const intro = document.createElement('div')
    intro.className = 'mb-14'
    intro.appendChild(sectionTitle('company'))
    localizedArray(detail.companyIntro).forEach((text) => {
      const paragraph = document.createElement('p')
      paragraph.className = 'text-lg text-slate-700 leading-relaxed mb-4'
      paragraph.textContent = text
      intro.appendChild(paragraph)
    })
    content.appendChild(intro)
  }
  // Why this role exists — omitted entirely by postings that predate it
  const reasons = createReasons(detail)
  if (reasons) content.appendChild(reasons)
  // Milestones
  if (detail.companyStats) content.appendChild(createRows('funding', detail.companyStats))
  content.appendChild(createMilestones(detail.milestones))
  if (detail.projects) content.appendChild(createRows('projects', detail.projects))
  if (detail.relatedPositions) content.appendChild(createRelatedPositions(position))
  // Conditions
  content.appendChild(createRows('conditions', detail.conditions))
  // Apply
  if (getEffectiveStatus(position) === 'closed') {
    const notice = document.createElement('p')
    notice.className = 'mb-14 p-6 rounded-2xl bg-slate-100 text-slate-600'
    notice.textContent = t('career.detail.closedNotice')
    content.appendChild(notice)
  } else {
    content.appendChild(createRows('apply', detail.apply))
  }
  // Apply button — dropped once the posting is closed, mirroring the listing
  // card, so a past deadline never leaves a live "Apply" button behind.
  if (getEffectiveStatus(position) !== 'closed') {
    content.appendChild(createApplyCta(position, detail))
  }
  // Process
  content.appendChild(createProcess(detail))
  // Footer links
  content.appendChild(createFooterLinks())

  section.appendChild(Container({ children: content }))
  return section
}

function createRelatedPositions(current: Position): HTMLElement {
  const block = document.createElement('div')
  block.className = 'mb-14'
  block.appendChild(sectionTitle('openRoles'))

  const note = document.createElement('p')
  note.className = 'text-slate-600 leading-relaxed mb-6'
  note.textContent = t('career.detail.relatedNote')
  block.appendChild(note)

  const links = document.createElement('div')
  links.className = 'flex flex-wrap gap-3'
  getVisiblePositions().forEach((position) => {
    if (position.id === current.id) {
      const label = document.createElement('span')
      label.className = 'px-5 py-3 rounded-full bg-orange-50 text-orange-700 font-semibold'
      label.textContent = `${localizedText(position.title)} · ${t('career.detail.currentPosting')}`
      links.appendChild(label)
    } else {
      links.appendChild(Button({
        children: localizedText(position.title),
        variant: 'secondary',
        href: `/company/career/${position.id}`,
      }))
    }
  })
  block.appendChild(links)
  return block
}

function createListBlock(
  key: string,
  data: BilingualArray,
  marker: 'disc' | 'check'
): HTMLElement {
  const block = document.createElement('div')
  block.className = 'mb-14'
  block.appendChild(sectionTitle(key))

  const list = document.createElement('ul')
  list.className = 'space-y-3'

  localizedArray(data).forEach((item) => {
    const li = document.createElement('li')
    li.className = 'flex items-start gap-3 text-lg text-slate-700 leading-relaxed'

    const icon = document.createElement('span')
    if (marker === 'check') {
      icon.className = 'mt-1 flex-shrink-0 text-orange-500'
      icon.innerHTML = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg>'
    } else {
      icon.className = 'mt-2.5 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-orange-500'
    }

    const text = document.createElement('span')
    text.textContent = item

    li.appendChild(icon)
    li.appendChild(text)
    list.appendChild(li)
  })

  block.appendChild(list)
  return block
}

/**
 * "Why this role exists" — up to three narrative cards, each with a fixed
 * kicker label. Returns null when the posting defines none of them.
 */
function createReasons(detail: PositionDetail): HTMLElement | null {
  const blocks: Array<[string, ReasonBlock]> = []
  if (detail.whyThisRole) blocks.push(['whyThisRole', detail.whyThisRole])
  if (detail.yourImpact) blocks.push(['yourImpact', detail.yourImpact])
  if (detail.whatYouGet) blocks.push(['whatYouGet', detail.whatYouGet])
  if (blocks.length === 0) return null

  const block = document.createElement('div')
  block.className = 'mb-14'
  block.appendChild(sectionTitle('why'))

  const stack = document.createElement('div')
  stack.className = 'space-y-4'

  blocks.forEach(([key, reason]) => {
    const card = document.createElement('div')
    card.className = 'rounded-2xl border border-slate-200 border-l-4 border-l-orange-500 bg-white p-6 sm:p-7'

    const kicker = document.createElement('p')
    kicker.className = 'text-xs font-bold text-orange-600 uppercase tracking-widest mb-2.5'
    kicker.textContent = t(`career.detail.reasons.${key}`)

    const title = document.createElement('h3')
    title.className = 'text-lg font-bold text-slate-900 leading-snug mb-3'
    title.textContent = localizedText(reason.title)

    const body = document.createElement('p')
    body.className = 'text-slate-700 leading-relaxed'
    body.textContent = localizedText(reason.body)

    card.appendChild(kicker)
    card.appendChild(title)
    card.appendChild(body)
    stack.appendChild(card)
  })

  block.appendChild(stack)
  return block
}

function createMilestones(milestones: Milestone[]): HTMLElement {
  const block = document.createElement('div')
  block.className = 'mb-14'
  block.appendChild(sectionTitle('milestones'))

  const wrap = document.createElement('div')
  wrap.className = 'border-l-2 border-orange-200 pl-6 space-y-6'

  milestones.forEach((m) => {
    const item = document.createElement('div')
    item.className = 'relative'

    const dot = document.createElement('span')
    dot.className = 'absolute -left-[1.95rem] top-1.5 w-3 h-3 rounded-full bg-orange-500 ring-4 ring-orange-100'
    item.appendChild(dot)

    const date = document.createElement('p')
    date.className = 'text-sm font-bold text-orange-600 mb-1'
    date.textContent = typeof m.date === 'string' ? m.date : localizedText(m.date)

    const text = document.createElement('p')
    text.className = 'text-slate-700 leading-relaxed'
    text.textContent = localizedText(m.text)

    item.appendChild(date)
    item.appendChild(text)
    wrap.appendChild(item)
  })

  block.appendChild(wrap)
  return block
}

function createRows(key: string, rows: LabeledRow[]): HTMLElement {
  const block = document.createElement('div')
  block.className = 'mb-14'
  block.appendChild(sectionTitle(key))

  const table = document.createElement('div')
  table.className = 'rounded-2xl border border-slate-200 overflow-hidden'

  rows.forEach((row, i) => {
    const r = document.createElement('div')
    r.className = `flex flex-col sm:flex-row ${i > 0 ? 'border-t border-slate-200' : ''}`

    const label = document.createElement('div')
    label.className = 'sm:w-44 flex-shrink-0 px-5 py-4 bg-slate-50 text-sm font-semibold text-slate-600'
    label.textContent = localizedText(row.label)

    const value = document.createElement('div')
    value.className = 'px-5 py-4 text-slate-800 leading-relaxed'
    value.textContent = localizedText(row.value)

    r.appendChild(label)
    r.appendChild(value)
    table.appendChild(r)
  })

  block.appendChild(table)
  return block
}

const MAIL_ICON =
  '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>'
const EXTERNAL_ICON =
  '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>'

const PRIMARY_CTA_CLASS =
  'inline-flex items-center justify-center gap-2 px-8 py-4 bg-orange-600 hover:bg-orange-700 text-white font-semibold text-base rounded-full transition-colors shadow-md hover:shadow-lg'

/**
 * Apply CTA. Saramin is the primary channel where a posting has one; email is
 * kept as a fallback but the address is always rendered as visible text.
 *
 * Why the address is spelled out: a bare `mailto:` link is a dead end for
 * anyone whose browser has no mail handler registered — the click does
 * nothing at all, with no error. That is exactly what happened here, and an
 * applicant has no way to tell the button apart from a broken page.
 */
function createApplyCta(position: Position, detail: PositionDetail): HTMLElement {
  const wrap = document.createElement('div')
  wrap.className = 'mb-14'

  // Per-posting prefix — the experience level differs by role, so it cannot be
  // derived from the title (e.g. the modeler opening is not "경력").
  const subject = encodeURIComponent(localizedText(detail.applySubject))
  const mailtoHref = `mailto:${detail.email}?subject=${subject}`

  // Micro-conversion. The default <a> action (external navigation or mailto:)
  // proceeds; gtag's sendBeacon is not affected by the page going away.
  const track = (channel: 'saramin' | 'email') => () => {
    trackEvent('career_apply_click', { position_id: position.id, apply_channel: channel })
  }

  if (detail.saraminUrl) {
    const primary = document.createElement('a')
    primary.href = detail.saraminUrl
    primary.target = '_blank'
    primary.rel = 'noopener noreferrer'
    primary.className = PRIMARY_CTA_CLASS
    primary.innerHTML = `${EXTERNAL_ICON} ${t('career.detail.applySaramin')}`
    primary.addEventListener('click', track('saramin'))
    wrap.appendChild(primary)

    const alt = document.createElement('p')
    alt.className = 'mt-4 text-sm text-slate-600'
    const altLink = document.createElement('a')
    altLink.href = mailtoHref
    altLink.className = 'text-orange-600 hover:text-orange-700 font-medium underline'
    altLink.textContent = detail.email
    altLink.addEventListener('click', track('email'))
    alt.append(`${t('career.detail.applyByEmail')} `, altLink)
    wrap.appendChild(alt)

    return wrap
  }

  const btn = document.createElement('a')
  btn.href = mailtoHref
  btn.className = PRIMARY_CTA_CLASS
  btn.innerHTML = `${MAIL_ICON} ${t('career.detail.applyButton')}`
  btn.addEventListener('click', track('email'))
  wrap.appendChild(btn)

  const note = document.createElement('p')
  note.className = 'mt-4 text-sm text-slate-600'
  note.textContent = `${t('career.detail.applyByEmail')} ${detail.email}`
  wrap.appendChild(note)

  return wrap
}

function createProcess(detail: PositionDetail): HTMLElement {
  const block = document.createElement('div')
  block.className = 'mb-14'
  block.appendChild(sectionTitle('process'))

  const flow = document.createElement('div')
  flow.className = 'flex flex-wrap items-center gap-3'

  const steps = localizedArray(detail.process.steps)
  steps.forEach((step, i) => {
    const chip = document.createElement('span')
    chip.className = 'px-4 py-2 rounded-full bg-slate-100 text-slate-700 font-medium text-sm'
    chip.textContent = step
    flow.appendChild(chip)

    if (i < steps.length - 1) {
      const arrow = document.createElement('span')
      arrow.className = 'text-orange-400'
      arrow.innerHTML = '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>'
      flow.appendChild(arrow)
    }
  })

  const note = document.createElement('p')
  note.className = 'mt-4 text-sm text-slate-500'
  note.textContent = localizedText(detail.process.note)

  block.appendChild(flow)
  block.appendChild(note)
  return block
}

function createFooterLinks(): HTMLElement {
  const wrap = document.createElement('div')
  wrap.className = 'mt-4 pt-8 border-t border-slate-200 flex flex-wrap items-center justify-between gap-4'

  const back = Button({
    children: t('career.detail.backToList'),
    variant: 'secondary',
    href: '/company/career',
  })
  wrap.appendChild(back)

  // The company story is the same for every posting, so it is linked rather
  // than duplicated into each one.
  const about = document.createElement('a')
  about.href = '/company/about'
  about.className = 'text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors'
  about.textContent = t('career.detail.aboutCompany')
  about.addEventListener('click', (e) => {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('navigate', { detail: '/company/about' }))
  })
  wrap.appendChild(about)

  return wrap
}

function createNotFound(): HTMLElement {
  const main = document.createElement('div')
  main.className = 'min-h-screen flex items-center justify-center bg-slate-50'

  const content = document.createElement('div')
  content.className = 'text-center'

  const title = document.createElement('h1')
  title.className = 'text-4xl font-bold text-slate-900 mb-6'
  title.textContent = '404'

  const btn = Button({
    children: t('career.detail.backToList'),
    variant: 'primary',
    href: '/company/career',
  })

  content.appendChild(title)
  content.appendChild(btn)
  main.appendChild(content)
  return main
}

function formatDeadline(deadline: string | null): string {
  if (deadline === null) return t('career.untilFilled')
  const locale = getCurrentLanguage() === 'en' ? 'en-US' : 'ko-KR'
  return new Date(deadline).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}
